import { useEffect, useState, useRef } from "react";
import { useAuthContext } from "../context/auth";
import useAudio from "./useAudio";

const useCountDown = (
  initialValue: number,
  callback: () => void,
  playSound?: boolean,
  resetOnComplete?: boolean // When true, reset to initialValue after reaching 0
) => {
  const { did } = useAuthContext();
  const [count, setCount] = useState(initialValue);
  const [isRunning, setIsRunning] = useState(false);
  const play = useAudio("countdown.mp3");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const syncStartTimeRef = useRef<number | null>(null);
  const pausedAtRef = useRef<number>(initialValue); // Track elapsed time when paused

  const start = (syncStartTime?: number, resumeFromPaused?: boolean) => {
    // 이미 실행 중이면 무시 (중복 호출로 인한 시간 점프 방지)
    if (isRunning) {
      console.log("[useCountDown] Already running, ignoring start call");
      return;
    }

    const now = syncStartTime || Date.now();

    if (resumeFromPaused && pausedAtRef.current < initialValue) {
      // Resume from where we paused: adjust start time based on remaining count
      const elapsedSeconds = initialValue - pausedAtRef.current;
      syncStartTimeRef.current = now - elapsedSeconds * 1000;
    } else {
      // Fresh start
      syncStartTimeRef.current = now;
      pausedAtRef.current = initialValue;
      setCount(initialValue); // 새로 시작할 때 count도 초기화
    }

    setIsRunning(true);
  };

  const stop = () => {
    // Save current count when stopping
    pausedAtRef.current = count;
    setIsRunning(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const reset = () => {
    setCount(initialValue);
    pausedAtRef.current = initialValue;
  };

  useEffect(() => {
    if (isRunning && syncStartTimeRef.current) {
      const startTime = syncStartTimeRef.current;

      const tick = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;

        // Calculate how many full seconds have passed
        const secondsPassed = Math.floor(elapsed / 1000);
        // Count down from initialValue
        const newCount = Math.max(0, initialValue - secondsPassed);

        setCount((prevCount) => {
          // 카운트가 증가하는 것 방지 (중복 start 호출 등으로 인한 버그 방지)
          if (newCount > prevCount) {
            console.log(
              "[useCountDown] Prevented count increase:",
              prevCount,
              "->",
              newCount
            );
            return prevCount;
          }

          // Only log and trigger actions when count actually changes
          if (newCount !== prevCount) {
            console.log("[useCountDown] Count:", newCount);

            // Play sound when count is 4 or less (to handle timing issues)
            if (newCount === 4) {
              if (playSound) {
                console.log("[useCountDown] Playing sound...");
                if (did === "1") {
                  play();
                  console.log("[useCountDown] Sound played!");
                } else {
                  console.log(
                    "[useCountDown] Skipped sound, did is not '1':",
                    did
                  );
                }
              }
            }

            if (initialValue === 4 && newCount === 3) {
              if (did === "1") {
                play();
              }
            }

            if (newCount === 0) {
              console.log("[useCountDown] Count is 0, calling callback");
              callback();
              setIsRunning(false);
              // If resetOnComplete is true, reset to initialValue, otherwise stay at 0
              return resetOnComplete ? initialValue : 0;
            }
          }

          return newCount;
        });

        // Calculate next tick to align with second boundaries
        const nextExpectedTime =
          startTime + (Math.floor(elapsed / 1000) + 1) * 1000;
        const delay = nextExpectedTime - Date.now();

        if (newCount > 0) {
          timerRef.current = setTimeout(tick, Math.max(0, delay));
        }
      };

      // Start immediately to show initial count
      tick();

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [
    isRunning,
    initialValue,
    play,
    callback,
    did,
    playSound,
    resetOnComplete,
  ]);

  return { count, start, stop, reset, stopped: !isRunning };
};

export default useCountDown;
